import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertCircle, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import {
  useMipsCategoryScores,
  usePaymentAdjustmentConfig,
  usePracticeEligibility,
} from "@/hooks/queries/useMipsQueries";

// Import the report sub-components from gap-management
import { ScoreGauge } from "@/components/gap-management/payment-adjustment/ScoreGauge";
import { PaymentCurveChart } from "@/components/gap-management/payment-adjustment/PaymentCurveChart";
import { GroupVsIndividualComparison } from "@/components/gap-management/payment-adjustment/GroupVsIndividualComparison";
import { ClinicianBreakdownTable, type ClinicianData } from "@/components/gap-management/payment-adjustment/ClinicianBreakdownTable";
import { ImprovementOpportunity } from "@/components/gap-management/payment-adjustment/ImprovementOpportunity";

interface PaymentConfig {
  performance_year: number;
  payment_year: number;
  low_scenario_input: number;
  scaling_factor: number;
}

interface PracticeEligibility {
  allowed_charges: number;
  practice_name: string;
  tin: string;
}

interface ClinicianEligibility {
  npi: string;
  clinician_name: string;
  clinician_type: string;
  mips_eligible_individual: boolean;
  mips_eligible_group: boolean;
  allowed_charges: number;
}

// Payment-adjustment math lives in the shared lib (R10 extract 2026-08-03)
import {
  computeAdjustmentPct as calculateAdjustment,
  determineZone,
  MAX_PENALTY,
} from "@/lib/payment-adjustment";

function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absAmount);
  
  if (amount < 0) return '-' + formatted;
  if (amount > 0) return '+' + formatted;
  return formatted;
}

// Loading skeleton for the page
function ReportSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="text-center">
            <Skeleton className="h-4 w-32 mx-auto mb-2" />
            <Skeleton className="h-16 w-20 mx-auto" />
          </div>
          <Skeleton className="h-4 w-full" />
        </div>
        <div className="rounded-2xl bg-muted p-6">
          <Skeleton className="h-4 w-40 mx-auto mb-3" />
          <Skeleton className="h-12 w-32 mx-auto mb-2" />
          <Skeleton className="h-3 w-48 mx-auto" />
        </div>
      </div>
      <Separator />
      <Skeleton className="h-48 w-full rounded-lg" />
      <Separator />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
      </div>
    </div>
  );
}

export default function PaymentAdjustmentReport() {
  const navigate = useNavigate();
  const { selectedPractice } = useAuth();
  const { programYear } = useProgramYear();

  const practiceId = selectedPractice?.id;
  const paymentYear = programYear + 2;

  // [Snappiness Phase 1a] Shared React Query pipeline: the composite (weights/
  // scores/IA/cost) is one cache entry per practice/year shared with Insights
  // and the Gap Management header; config + eligibility load IN PARALLEL with
  // it (previously this page chained 3-4 sequential round trips).
  const { compositeScore, loading: scoresLoading } = useMipsCategoryScores(practiceId, programYear);
  const configQ = usePaymentAdjustmentConfig(programYear);
  const eligibilityQ = usePracticeEligibility(practiceId, programYear);

  const loading = !!practiceId && (scoresLoading || configQ.isPending || eligibilityQ.isPending);

  // null after fetch = config missing for this year (old explicit-404 path)
  const config: PaymentConfig | null = configQ.data ?? null;
  const error = !loading && configQ.data === null
    ? 'Payment adjustment configuration not found for this year. Please contact your administrator.'
    : null;

  const eligibilityData = eligibilityQ.data;
  const hasEligibilityData = Boolean(eligibilityData?.imported && eligibilityData?.practice);
  const practiceEligibility: PracticeEligibility | null = hasEligibilityData
    ? {
        allowed_charges: eligibilityData.practice.allowed_charges || 0,
        practice_name: eligibilityData.practice.practice_name || '',
        tin: eligibilityData.practice.tin || '',
      }
    : null;
  const clinicians: ClinicianEligibility[] = hasEligibilityData
    ? (eligibilityData.clinicians || [])
    : [];

  const score = useMemo(
    () => Math.round(Math.min(100, Math.max(0, compositeScore))),
    [compositeScore],
  );

  // Calculate values
  const medicareRevenue = practiceEligibility?.allowed_charges || 0;
  const lowScenarioInput = config?.low_scenario_input || 2.5;
  const scalingFactor = config?.scaling_factor || 0.74;
  
  const adjustmentPercent = calculateAdjustment(score, lowScenarioInput, scalingFactor);
  const dollarAmount = medicareRevenue * adjustmentPercent;
  const zone = determineZone(score);

  // Calculate group vs individual comparison
  const groupScore = score;
  const groupDollarImpact = dollarAmount;

  // Process clinician data
  const eligibleClinicians: ClinicianData[] = clinicians
    .filter(c => c.mips_eligible_individual)
    .map(c => {
      const clinicianScore = score;
      const clinicianAdjustment = calculateAdjustment(clinicianScore, lowScenarioInput, scalingFactor);
      const clinicianImpact = (c.allowed_charges || 0) * clinicianAdjustment;
      
      return {
        npi: c.npi,
        name: c.clinician_name,
        specialty: c.clinician_type,
        mipsEligible: true,
        score: clinicianScore,
        medicareRevenue: c.allowed_charges || 0,
        adjustmentPercent: clinicianAdjustment,
        dollarImpact: clinicianImpact,
        zone: determineZone(clinicianScore),
      };
    });

  const ineligibleClinicians: ClinicianData[] = clinicians
    .filter(c => !c.mips_eligible_individual)
    .map(c => ({
      npi: c.npi,
      name: c.clinician_name,
      specialty: c.clinician_type,
      mipsEligible: false,
      score: null,
      medicareRevenue: c.allowed_charges || 0,
      reason: 'Not MIPS eligible as individual',
    }));

  const individualTotal = eligibleClinicians.reduce((sum, c) => sum + (c.dollarImpact || 0), 0);
  const difference = individualTotal - groupDollarImpact;
  const recommendation: 'GROUP' | 'INDIVIDUAL' | 'EITHER' = 
    difference > 100 ? 'INDIVIDUAL' : 
    difference < -100 ? 'GROUP' : 
    'EITHER';

  return (
    <>
      <Helmet>
        <title>Payment Adjustment Report | MIPS Insights</title>
        <meta name="description" content="View projected MIPS payment adjustments and compare group vs individual reporting options." />
      </Helmet>

      <div className="space-y-6">
        {/* Header with back navigation */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/scorecards')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Insights
          </Button>
        </div>

        {/* Page Title */}
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <DollarSign className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {programYear} MIPS Payment Adjustment Projection
            </h1>
            <p className="text-muted-foreground">
              {practiceEligibility?.practice_name || selectedPractice?.tin_name || 'Practice'} • 
              Payment Year {paymentYear}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="rounded-lg border bg-card p-6">
          {loading ? (
            <ReportSkeleton />
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-6">
              {/* Eligibility Warning */}
              {!hasEligibilityData && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Medicare revenue data is not available. To see accurate projections, please upload your QPP eligibility file in{' '}
                    <strong>Configuration → QPP Reports</strong>.
                  </AlertDescription>
                </Alert>
              )}

              {/* Score and Projection Header */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Score Display */}
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">MIPS Composite Score</p>
                    <p className="text-6xl font-bold mt-1">{score}</p>
                  </div>
                  <ScoreGauge score={score} />
                </div>

                {/* Projection Display */}
                <div className={cn(
                  "rounded-2xl p-6 flex flex-col items-center justify-center",
                  dollarAmount < 0 ? "bg-red-50 dark:bg-red-900/20" :
                  dollarAmount > 0 ? "bg-emerald-50 dark:bg-emerald-900/20" :
                  "bg-muted"
                )}>
                  <p className="text-sm text-muted-foreground mb-2">
                    {dollarAmount >= 0 ? 'Projected Bonus' : 'Projected Penalty'}
                  </p>
                  <p className={cn(
                    "text-5xl font-bold",
                    dollarAmount < 0 ? "text-red-600 dark:text-red-400" :
                    dollarAmount > 0 ? "text-emerald-600 dark:text-emerald-400" :
                    "text-muted-foreground"
                  )}>
                    {hasEligibilityData ? formatCurrency(dollarAmount) : '—'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {(adjustmentPercent * 100).toFixed(2)}% adjustment (Medium scenario)
                  </p>
                  {hasEligibilityData && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Based on {formatCurrency(medicareRevenue).replace(/[+-]/, '')} Medicare revenue
                    </p>
                  )}
                </div>
              </div>

              {/* Total Financial Impact Banner - for bonus zone */}
              {hasEligibilityData && score > 75 && (
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-gradient-to-r from-emerald-50/80 via-blue-50/50 to-emerald-50/80 dark:from-emerald-900/10 dark:via-blue-900/10 dark:to-emerald-900/10 px-5 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Projected Value of Performance:</span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Bonus:</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(dollarAmount)}</span>
                      </div>
                      <span className="text-muted-foreground">+</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Penalty avoided:</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(Math.abs(medicareRevenue * MAX_PENALTY)).replace(/[+-]/, '')}</span>
                      </div>
                      <span className="text-muted-foreground">=</span>
                      <div className="flex items-center gap-1.5 pl-2 border-l border-slate-300 dark:border-slate-600">
                        <span className="font-bold text-lg text-slate-800 dark:text-slate-200">
                          {formatCurrency(dollarAmount + Math.abs(medicareRevenue * MAX_PENALTY)).replace(/[+-]/, '')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              {/* Payment Curve Chart */}
              {hasEligibilityData && config && (
                <>
                  <div>
                    <h3 className="text-sm font-medium mb-3">Payment Adjustment Curve</h3>
                    <PaymentCurveChart
                      currentScore={score}
                      lowScenarioInput={lowScenarioInput}
                      medicareRevenue={medicareRevenue}
                      scalingFactor={scalingFactor}
                    />
                  </div>
                  <Separator />
                </>
              )}

              {/* Group vs Individual Comparison */}
              <GroupVsIndividualComparison
                groupScore={groupScore}
                groupDollarImpact={groupDollarImpact}
                groupZone={zone}
                individualTotal={individualTotal}
                eligibleClinicianCount={eligibleClinicians.length}
                ineligibleClinicianCount={ineligibleClinicians.length}
                recommendation={recommendation}
                differenceAmount={Math.abs(difference)}
                hasEligibilityData={hasEligibilityData && clinicians.length > 0}
              />

              {/* Clinician Breakdown */}
              {hasEligibilityData && clinicians.length > 0 && (
                <>
                  <Separator />
                  <ClinicianBreakdownTable
                    eligibleClinicians={eligibleClinicians}
                    ineligibleClinicians={ineligibleClinicians}
                  />
                </>
              )}

              <Separator />

              {/* Improvement Opportunity */}
              {hasEligibilityData && config && (
                <ImprovementOpportunity
                  currentScore={score}
                  currentAmount={dollarAmount}
                  medicareRevenue={medicareRevenue}
                  lowScenarioInput={lowScenarioInput}
                  scalingFactor={scalingFactor}
                />
              )}

              {/* Disclaimer */}
              <div className="rounded-lg bg-muted/50 p-4 text-xs text-muted-foreground">
                <p className="font-medium mb-2">Important:</p>
                <p>
                  These projections are estimates based on current MIPS scores and Medicare Part B allowed charges. 
                  Actual payment adjustments are applied to claims during the payment year and depend on CMS final 
                  calculations including budget neutrality scaling. Three scenarios (low/expected/high) reflect 
                  uncertainty in these factors. Non-MIPS-eligible clinicians do not receive individual adjustments 
                  but contribute to group performance when reporting as a TIN.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

